import { useCurrents, useNotifications, useRenewals } from '../useData';
import { RenewalInfo, RenewalProduct, amountOf, productName, useRenewalProducts } from '../products';

export interface SummaryLine {
  key: string;
  label: string;
  level: number;
  isTotal?: boolean;
}

export type SummaryValues = Record<string, number>;
export type SummaryType = SummaryValues | null | undefined;

const customersKey = 'customers';
const totalKey = 'total';
const productKey = (product: string | null | undefined) => `product:${product ?? 'none'}`;
const subscriptionKey = (number: string) => `number:${number}`;

export const isCustomersLine = (line: SummaryLine) => line.key === customersKey;

export function summaryLines(products: RenewalProduct[]): SummaryLine[] {
  const lines: SummaryLine[] = [{ key: customersKey, label: 'Antall kunder', level: 0 }];
  let current: string | undefined;
  for (const p of products) {
    const key = productKey(p.product);
    if (key !== current) {
      lines.push({ key, label: productName(p.product), level: 0 });
      current = key;
    }
    lines.push({ key: subscriptionKey(p.number), label: p.name, level: p.level + 1 });
  }
  lines.push({ key: totalKey, label: 'Totalt', level: 0, isTotal: true });
  return lines;
}

export function useSummaryLines(): SummaryLine[] | undefined {
  const products = useRenewalProducts();
  return products ? summaryLines(products) : undefined;
}

function useSummaryOf({ renewals, loading }: { renewals?: RenewalInfo[]; loading: boolean }) {
  const products = useRenewalProducts();
  return {
    summary: !loading && products ? createSummary(renewals ?? [], products) : undefined,
  };
}

export function useCurrentSummary() {
  return useSummaryOf(useCurrents());
}

export function useRenewalSummary() {
  return useSummaryOf(useRenewals());
}

export function useNotificationSummary() {
  return useSummaryOf(useNotifications());
}

export function useNotificationDiffSummary() {
  const { summary: notificationSummary } = useNotificationSummary();
  const { summary: currentSummary } = useCurrentSummary();

  return {
    summary: createComposite(notificationSummary, currentSummary, (summary, baseline) => baseline - summary),
  };
}

export function useNotificationDiffPercentageSummary() {
  const { summary: notificationSummary } = useNotificationSummary();
  const { summary: currentSummary } = useCurrentSummary();

  return {
    summary: createComposite(notificationSummary, currentSummary, (summary, baseline) => (baseline - summary) / summary),
  };
}

export function useRenewalDiffSummary() {
  const { summary: renewalSummary } = useRenewalSummary();
  const { summary: currentSummary } = useCurrentSummary();

  return {
    summary: createComposite(renewalSummary, currentSummary, (summary, baseline) => baseline - summary),
  };
}

function createComposite(summary: SummaryType, baseline: SummaryType, calculate: (summary: number, baseline: number) => number): SummaryType {
  if (!summary || !baseline) return undefined;
  const keys = new Set([...Object.keys(summary), ...Object.keys(baseline)]);
  return Object.fromEntries([...keys].map((key) => [key, calculate(summary[key] ?? 0, baseline[key] ?? 0)]));
}

export function createSummary(renewals: RenewalInfo[], products: RenewalProduct[]): SummaryType {
  if (renewals.length === 0) return null;
  const sum = (select: (renewal: RenewalInfo) => number | undefined) => renewals.reduce((total, renewal) => total + (select(renewal) ?? 0), 0);

  const values: SummaryValues = { [customersKey]: renewals.length, [totalKey]: sum((x) => x.total) };
  for (const p of products) {
    const amount = sum((x) => amountOf(x, p.number));
    values[subscriptionKey(p.number)] = amount;
    values[productKey(p.product)] = (values[productKey(p.product)] ?? 0) + amount;
  }
  return values;
}
